// 목록 검색·페이지의 규칙 한 벌 — 인메모리·Supabase 저장소가 같이 쓴다(검색어 토막·열 일치·쪽 계산·건수).

export const LIST_PAGE_SIZE = 20;
export const SEARCH_MAX_TOKENS = 5;
const SEARCH_TOKEN_MAX = 50;
// 고객 이름으로 찾을 때 한 검색이 끌어오는 고객 수 합(토막마다 나눠 씀) — 최악(5토막×50자 한글×4열 + id 100개) 주소 ≈ 14KB, 실측 한계 32KB(24KB 통과)에 닿지 않는다.
export const CUSTOMER_NAME_MATCH_BUDGET = 100;

// 토막 하나가 끌어올 고객 수 — 토막 하나면 100, 다섯이면 20.
export const nameMatchCap = (tokenCount: number): number =>
  Math.floor(CUSTOMER_NAME_MATCH_BUDGET / Math.max(1, tokenCount));

// 공백과 PostgREST `or()` 문법(`,()"\`)·LIKE 와일드카드(`%_`)·PostgREST 별표(`*`)는 토막을 가르는 자리다.
const TOKEN_SPLIT = /[\s,()"\\%_*]+/u;

export const searchTokens = (q: string | null | undefined): string[] => {
  const out: string[] = [];
  for (const raw of (q ?? "").toLowerCase().split(TOKEN_SPLIT)) {
    const token = raw.slice(0, SEARCH_TOKEN_MAX);
    if (token && !out.includes(token)) out.push(token);
    if (out.length === SEARCH_MAX_TOKENS) break;
  }
  return out;
};

// 토막마다 어느 한 열에라도 들어 있어야 한다(토막끼리 AND · 열끼리 OR · 대소문자 무시) — Supabase 의 ilike 와 같은 뜻.
export const matchesTokens = (
  values: readonly (string | null | undefined)[],
  tokens: readonly string[],
  extra?: (token: string) => boolean
): boolean =>
  tokens.every(
    (token) => values.some((v) => typeof v === "string" && v.toLowerCase().includes(token)) || (extra?.(token) ?? false)
  );

export interface NameMatch {
  ids: string[];
  // 상한을 넘게 맞았다 — 뒤쪽 고객의 줄은 검색에서 빠졌을 수 있다(화면이 알린다).
  capped: boolean;
}

// id 순으로 상한+1 개까지 받은 것을 상한으로 자르고 넘쳤는지 적는다 — 두 저장소가 같은 판정을 쓴다.
export const capNameMatch = (sortedIds: readonly string[], cap: number): NameMatch => ({
  ids: sortedIds.slice(0, cap),
  capped: sortedIds.length > cap,
});

// 이름에 토막이 든 고객 id — id 순 앞에서 상한까지(Supabase 의 `order(id).limit(cap + 1)` 과 같은 결과).
export const customerIdsByName = (
  customers: readonly { id: string; name: string }[],
  token: string,
  cap: number
): NameMatch =>
  capNameMatch(
    customers
      .filter((c) => c.name.toLowerCase().includes(token))
      .map((c) => c.id)
      .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
    cap
  );

// URL 의 `?page=` — 1 이상 정수만, 아니면 1.
export const parsePage = (value: string | string[] | undefined): number => {
  const raw = Array.isArray(value) ? value[0] : value;
  const n = raw && /^\d{1,6}$/.test(raw) ? Number(raw) : 1;
  return n >= 1 ? n : 1;
};

export const pageCountOf = (total: number, size: number = LIST_PAGE_SIZE): number =>
  Math.max(1, Math.ceil(total / size));

export interface ListPage<T, K extends string> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  // 검색어·범위 조건까지 건 상태에서 칩별 건수 — ALL 은 칩 조건을 빼고 센 것.
  counts: Record<K | "ALL", number>;
  // 고객 이름이 상한을 넘게 맞아 일부 고객의 줄만 찾았다.
  nameMatchCapped: boolean;
}

export interface ListQuery {
  q?: string;
  page?: number;
}

// 한 목록의 규칙 — scope 는 건수에도 먹는 조건, keep 은 칩 조건(건수는 이 조건을 빼고 센다).
export interface ListSpec<R extends { id: string }, Q extends ListQuery, K extends string> {
  columns: readonly (keyof R & string)[];
  byCustomerName: boolean;
  // Supabase 가 쪽을 가르기 전에 받는 가벼운 열 — scope·keep·keysOf·compare 가 읽는 열 전부.
  light: string;
  keys: readonly K[];
  keysOf: (row: R, query: Q) => K[];
  scope: (row: R, query: Q) => boolean;
  keep: (row: R, query: Q) => boolean;
  compare: (a: R, b: R) => number;
  // 조회마다 달라지는 순서(현장 「오늘 할 일」) — 있으면 compare 대신 쓴다.
  compareFor?: (query: Q) => ((a: R, b: R) => number) | null;
  // 줄끼리 봐야 하는 조건(견적 「계열 최신」) — 검색 전 모든 줄에서 고른다(null 이면 걸지 않음) · 건수·칩에도 먹는다.
  narrow?: (query: Q) => ((rows: readonly R[]) => R[]) | null;
}

export interface PagePlan<K extends string> {
  ids: string[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  counts: Record<K | "ALL", number>;
}

// 검색이 계열 일부만 남겨도 머리를 잘못 뽑지 않게 — 검색 전 모든 줄(all)에서 고른 뒤 검색에 걸린 줄만 남긴다.
export const narrowRows = <R extends { id: string }, Q extends ListQuery, K extends string>(
  searched: readonly R[],
  all: readonly R[],
  spec: ListSpec<R, Q, K>,
  query: Q
): R[] => {
  const pick = spec.narrow?.(query);
  if (!pick) return [...searched];
  const found = new Set(searched.map((row) => row.id));
  return pick(all).filter((row) => found.has(row.id));
};

// 검색어로 이미 거른 줄에서 건수를 세고, 칩 조건·정렬을 건 뒤 이 쪽의 id 만 고른다 — all 은 narrow 가 읽는 검색 전 줄.
export const planPage = <R extends { id: string }, Q extends ListQuery, K extends string>(
  searched: readonly R[],
  spec: ListSpec<R, Q, K>,
  query: Q,
  pageSize: number = LIST_PAGE_SIZE,
  all: readonly R[] = searched
): PagePlan<K> => {
  const narrowed = narrowRows(searched, all, spec, query);
  const scoped = narrowed.filter((row) => spec.scope(row, query));
  const counts = { ALL: scoped.length } as Record<K | "ALL", number>;
  for (const key of spec.keys) counts[key] = 0;
  for (const row of scoped) for (const key of spec.keysOf(row, query)) counts[key] += 1;

  const kept = scoped.filter((row) => spec.keep(row, query)).sort(spec.compareFor?.(query) ?? spec.compare);
  const pageCount = pageCountOf(kept.length, pageSize);
  // 끝을 넘은 쪽(손으로 고친 `?page=99`)은 마지막 쪽으로 — 빈 표·강조 없는 쪽 이동이 나오지 않게.
  const page = Math.min(query.page && query.page >= 1 ? Math.floor(query.page) : 1, pageCount);
  const from = (page - 1) * pageSize;
  return {
    ids: kept.slice(from, from + pageSize).map((row) => row.id),
    total: kept.length,
    page,
    pageSize,
    pageCount,
    counts,
  };
};

// 저장소가 가진 모든 줄에 검색어를 건다 — 인메모리 구현의 검색. Supabase 는 같은 뜻을 ilike·in 으로 DB 에 보낸다.
export const searchRows = <R extends { id: string }>(
  rows: readonly R[],
  columns: readonly (keyof R & string)[],
  tokens: readonly string[],
  idsByToken?: ReadonlyMap<string, readonly string[]>
): R[] =>
  tokens.length === 0
    ? [...rows]
    : rows.filter((row) =>
        matchesTokens(
          columns.map((c) => row[c] as unknown as string | null | undefined),
          tokens,
          idsByToken
            ? (token) => {
                const customerId = (row as unknown as { customer_id?: string }).customer_id;
                return customerId !== undefined && (idsByToken.get(token) ?? []).includes(customerId);
              }
            : undefined
        )
      );

// 인메모리 한 번에: 검색 → 건수·칩·정렬·쪽 → 줄.
export const listPageOf = <R extends { id: string }, Q extends ListQuery, K extends string>(
  rows: readonly R[],
  spec: ListSpec<R, Q, K>,
  query: Q,
  customers: readonly { id: string; name: string }[] = []
): ListPage<R, K> => {
  const tokens = searchTokens(query.q);
  const matches = spec.byCustomerName
    ? tokens.map((t) => customerIdsByName(customers, t, nameMatchCap(tokens.length)))
    : [];
  const idsByToken = spec.byCustomerName ? new Map(tokens.map((t, i) => [t, matches[i]!.ids])) : undefined;
  const { ids, ...rest } = planPage(searchRows(rows, spec.columns, tokens, idsByToken), spec, query, LIST_PAGE_SIZE, rows);
  const byId = new Map(rows.map((row) => [row.id, row]));
  return {
    ...rest,
    rows: ids.flatMap((id) => byId.get(id) ?? []),
    nameMatchCapped: matches.some((m) => m.capped),
  };
};

// 목록 링크 — 빈 값은 빼고, page 는 1 이면 뺀다(첫 쪽 주소가 하나로 모인다).
export const listHref = (path: string, params: Record<string, string | number | null | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    if (key === "page" && Number(value) <= 1) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

// 쪽 안에서 바꾼 순서를 전체 순서의 그 쪽 자리(offset)에 끼운다 — 쪽 밖 줄의 자리는 그대로다.
export const spliceOrder = (allIds: readonly string[], pageIds: readonly string[], offset: number): string[] => {
  const onPage = new Set(pageIds);
  const rest = allIds.filter((id) => !onPage.has(id));
  rest.splice(Math.min(offset, rest.length), 0, ...pageIds);
  return rest;
};
